export const MAX_HISTORY_LENGTH = 30;

export type UndoRedoManagerOptions = {
    storageKey?: string;
    maxHistoryLength?: number;
};

export type HistoryItem = {
    sgfContent: string;
    currentNodeIndex: string;
};

export class UndoRedoManager {
    private history: HistoryItem[] = [];
    private index = -1;
    public storageKey = 'sgfHistory';
    private readonly maxHistoryLength: number;

    constructor({
        storageKey = 'sgfHistory',
        maxHistoryLength = MAX_HISTORY_LENGTH,
    }: UndoRedoManagerOptions = {}) {
        this.maxHistoryLength = maxHistoryLength;
        this.storageKey = storageKey;

        const storedData = localStorage.getItem(this.storageKey);
        if (storedData) {
            const {history, index} = JSON.parse(storedData);
            this.history = history.map((item: any) => ({
                sgfContent: item.sgfContent,
                currentNodeIndex: item.currentNodeIndex,
            }));
            this.index = index;
        }
    }

    public add(sgfContent: string, currentNodeIndex: string): void {
        const lastItem = this.history[this.index];

        if (!lastItem || lastItem.sgfContent !== sgfContent) {
            this.history = this.history.slice(0, this.index + 1);
            this.history.push({
                sgfContent,
                currentNodeIndex,
            });
            this.index = this.history.length - 1;
            this.trimHistory();
            this.saveHistoryToLocalStorage();
        }
    }

    public replace(sgfContent: string, currentNodeIndex: string): void {
        const lastItem = this.history[this.index];

        if (
            lastItem &&
            (lastItem.sgfContent !== sgfContent ||
                lastItem.currentNodeIndex !== currentNodeIndex)
        ) {
            this.history[this.index] = {
                sgfContent,
                currentNodeIndex,
            };
            this.saveHistoryToLocalStorage();
        }
    }

    public undo(): HistoryItem | null {
        if (this.canUndo()) {
            this.index -= 1;
            this.saveHistoryToLocalStorage();
            return this.getCurrentItem();
        }
        return null;
    }

    public redo(): HistoryItem | null {
        if (this.canRedo()) {
            this.index += 1;
            this.saveHistoryToLocalStorage();
            return this.getCurrentItem();
        }
        return null;
    }

    public canUndo(): boolean {
        return this.index > 0;
    }

    public canRedo(): boolean {
        return this.index < this.history.length - 1;
    }

    public clear(): void {
        this.history = [];
        this.index = -1;
        localStorage.removeItem(this.storageKey);
    }

    public getCurrentItem(): HistoryItem {
        return this.history[this.index];
    }

    public setStorageKey(storageKey: string): void {
        this.storageKey = storageKey;
    }

    public getStorageKey(): string {
        return this.storageKey;
    }

    private trimHistory(): void {
        const historyLength = this.history.length;
        if (historyLength > this.maxHistoryLength) {
            const excess = historyLength - this.maxHistoryLength;
            this.history = this.history.slice(excess);
            this.index -= excess;
            if (this.index < 0) {
                this.index = 0;
            }
        }
    }

    private saveHistoryToLocalStorage(): void {
        localStorage.setItem(
            this.storageKey,
            JSON.stringify({history: this.history, index: this.index})
        );
    }
}
